import { useEffect, useRef } from "react";
const useScrollBaseMonthChange = ({ isActive, selector, monthsCallback, daysCallback }) => {
    const datesIntersectionRef = useRef();
    const mutationRef = useRef();
    const timerRef = useRef();
    useEffect(() => {
        if (!isActive)
            return;
        const root = document.querySelector(`[${selector.key}="${selector.value}"]`);
        const scrollableElement = root?.lastChild;
        if (scrollableElement) {
            const handleScroll = () => {
                clearTimeout(timerRef.current);
                timerRef.current = setTimeout(() => {
                    const monthContainers = Array.from(scrollableElement.childNodes);
                    const rootRect = root?.getBoundingClientRect();
                    if (!rootRect)
                        return;
                    for (const container of monthContainers) {
                        if (container.nodeType !== Node.ELEMENT_NODE)
                            continue;
                        const containerRect = container.getBoundingClientRect();
                        const isVisible = containerRect.bottom > rootRect.top && containerRect.top < rootRect.bottom;
                        if (isVisible) {
                            const firstDate = container.querySelector("[data-date]");
                            if (firstDate) {
                                const dateStr = firstDate.getAttribute("data-date");
                                if (dateStr) {
                                    const date = new Date(dateStr);
                                    monthsCallback?.(date, "scroll");
                                    return;
                                }
                            }
                        }
                    }
                }, 200);
            };
            scrollableElement.addEventListener("scroll", handleScroll);
            handleScroll();
        }
        return () => {
            clearTimeout(timerRef.current);
        };
    }, [monthsCallback, isActive, selector.key, selector.value]);
    useEffect(() => {
        if (!isActive || !daysCallback)
            return;
        const root = document.querySelector(`[${selector.key}="${selector.value}"]`);
        const scrollableElement = root?.lastChild;
        if (scrollableElement) {
            mutationRef.current = new MutationObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.addedNodes.length) {
                        entry.addedNodes.forEach((node) => {
                            const dates = node.querySelectorAll("[data-date]");
                            if (dates.length) {
                                dates.forEach((node) => {
                                    if (node && datesIntersectionRef.current) {
                                        datesIntersectionRef.current.observe(node);
                                    }
                                });
                            }
                        });
                    }
                    if (entry.removedNodes.length) {
                        entry.removedNodes.forEach((node) => {
                            const dates = node.querySelectorAll("[data-date]");
                            if (dates.length) {
                                dates.forEach((node) => {
                                    if (node && datesIntersectionRef.current) {
                                        datesIntersectionRef.current.unobserve(node);
                                    }
                                });
                            }
                        });
                    }
                });
            });
            datesIntersectionRef.current = new IntersectionObserver((entries) => {
                const dates = {};
                entries.forEach((entry) => {
                    const date = entry.target.getAttribute("data-date");
                    if (entry.isIntersecting) {
                        if (date) {
                            dates[date] = 1;
                        }
                    }
                    else {
                        if (date) {
                            dates[date] = 0;
                        }
                    }
                });
                daysCallback((prev) => {
                    const newState = { ...prev };
                    Object.entries(dates).forEach(([key, value]) => {
                        if (value === 1) {
                            newState[key] = 1;
                        }
                        else {
                            delete newState[key];
                        }
                    });
                    return newState;
                });
            }, {
                root,
                threshold: 0
            });
            const dates = scrollableElement.querySelectorAll("[data-date]");
            if (dates.length) {
                dates.forEach((node) => {
                    if (node && datesIntersectionRef.current) {
                        datesIntersectionRef.current.observe(node);
                    }
                });
            }
            mutationRef.current.observe(scrollableElement, {
                childList: true
            });
        }
        return () => {
            mutationRef.current?.disconnect();
            datesIntersectionRef.current?.disconnect();
        };
    }, [daysCallback, isActive, selector.key, selector.value]);
};
export default useScrollBaseMonthChange;
